import { useEffect, useState } from 'react'

import { VscGraph } from 'react-icons/vsc'
import { useLocation, useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import { Banner, Text } from '@loadsmart/miranda-react'

import { TOKEN_STORAGE_USER_KEY } from 'auth/domain/User'
import { AnalyticsEvent } from 'core/analytics/events'
import { useAnalytics } from 'core/analytics/useAnalytics'
import { LocalStorageClient } from 'core/infra/storage/LocalStorageClient'
import AppLayout from 'core/ui/components/AppLayout'
import Header from 'core/ui/components/Header'
import { useInitApplication } from 'core/ui/hooks/useInitApplication'
import { PBIReport } from 'managed-analytics/domain/PBIReport'
import { RequestType } from 'managed-analytics/domain/RequestType'
import ReportComponent from 'managed-analytics/ui/components/report/Report'
import { usePBIReports } from 'managed-analytics/ui/hooks/usePBIReports'

import ZendeskDialog from './components/zendesk/ZendeskDialog'
import { usePBIToken } from './hooks/usePBIToken'
import { useRequestZendesk } from './hooks/useRequestZendesk'
import { StyledReportWrapper } from './ManagedAnalyticsPage.styles'

enum DialogFeedbackType {
  NONE = '',
  SUCCESS = 'success',
  FAILED = 'failed',
}

const BannerWrapper = styled.div`
  text-align: left;
`

const ManagedAnalytics = () => {
  const [selectedReport, setSelectedReport] = useState<PBIReport | undefined>()
  const [requestType, setRequestType] = useState<RequestType>()
  const [showRequestDialog, setShowRequestDialog] = useState(false)
  const [showDialogFeedback, setShowDialogFeedback] = useState(DialogFeedbackType.NONE)
  
  const [] = useState('Integrations')
  
  const { hasError, hasSuccess, isSendingRequest, requestZendesk } = useRequestZendesk()
  const { getReports, isLoading: isReportsLoading, reports } = usePBIReports()
  const { getPBIToken, resetPBIToken, isLoading: isTokenLoading, pbiToken } = usePBIToken()
  

  const location = useLocation()

  useEffect(() => {
    if (location.state?.showDialogFeedback) {
      setShowDialogFeedback(DialogFeedbackType.SUCCESS)

      window.history.replaceState({}, document.title)
    }
  }, [location.state])

  //mixpanel init
  const { track } = useAnalytics()
  const [freshRender, setFreshRender] = useState(true)
  useInitApplication()

  const initApplication = () => {
    const storedParams = JSON.parse(localStorage.getItem('fiai_redirect_params') || '{}')

    // if stored params, move the report to the position 0 of the list
    if (storedParams.reportName && reports.length > 0) {
      const matchingReport = reports.find(report => report.reportName === storedParams.reportName)
      if (matchingReport) {
        const index = reports.indexOf(matchingReport)
        reports.splice(index, 1)
        reports.unshift(matchingReport)
        setShowRefreshButton(true)
      }
    }
    if (reports) setSelectedReport(reports[0])
    if (reports[0] && freshRender) {
      const mixpanel_custom_props = {
        'Report Name': reports[0].reportName,
        'Dataset Id': reports[0].datasetId,
        'Dataset Group Id': reports[0].groupId,
        'PBI User Id': reports[0].rlsFilter,
        'PBI User Type': reports[0].customerType,
        'Role Name': reports[0].roleName,
        'Default Report': true,
      }
      track(AnalyticsEvent.OpenReport, mixpanel_custom_props)
      track(AnalyticsEvent.InitApplication)
      setFreshRender(false)
    }
  }

  const handleDismiss = () => {
    setShowDialogFeedback(DialogFeedbackType.NONE)
  }

  const handleOnSendFeedback = (description: string) => {
    if (!requestType) return
    const reportName: any = selectedReport?.reportName
    requestZendesk(requestType, reportName, description)
  }

  const [showRefreshButton, setShowRefreshButton] = useState(false)

  const handleRefreshReport = () => {
    setShowRefreshButton(false)
    localStorage.removeItem('fiai_redirect_params')
    setSelectedReport(undefined)
    setTimeout(() => {
      setSelectedReport(selectedReport)
    }, 500)
  }

  useEffect(() => {
    if (hasError) {
      setShowDialogFeedback(DialogFeedbackType.FAILED)
      setShowRequestDialog(false)
    }

    if (hasSuccess) {
      setShowDialogFeedback(DialogFeedbackType.SUCCESS)
      setShowRequestDialog(false)
    }
  }, [hasError, hasSuccess])

  const storage = new LocalStorageClient()
  const userAccessToken = storage.get(TOKEN_STORAGE_USER_KEY)

  const navigate = useNavigate()

  useEffect(() => {
    if (!userAccessToken) navigate('/')
  }, [navigate, userAccessToken])

  useEffect(() => {
    getReports()
  }, [getReports])

  useEffect(() => {
    initApplication()
  }, [track, reports])

  useEffect(() => {
    const updateToken = async () => {
      await resetPBIToken()
      getPBIToken(selectedReport)
    }

    updateToken()
  }, [getPBIToken, resetPBIToken, selectedReport])

  const uniqueRoles = Array.from(
    new Set(reports.map(report => report.roleName).filter(role => role != null))
  )
  const filteredReports = reports.filter(report => report.roleName === selectedReport?.roleName)

  useEffect(() => {
    if (uniqueRoles.length > 0 && !selectedReport) {
      const defaultRole = reports.find(report => report.roleName === uniqueRoles[0])
      if (defaultRole) {
        setSelectedReport(defaultRole)
      }
    }
  }, [uniqueRoles, reports, selectedReport])

  useEffect(() => {
    if (filteredReports.length > 0 && !selectedReport) {
      setSelectedReport(filteredReports[0])
    }
  }, [filteredReports, selectedReport])

  

  

  return (
    <AppLayout>
      <Header
        title={
          selectedReport && (
            <Text color="color-text-primary" variant="body-md-bold">
              {selectedReport.reportName}
            </Text>
          )
        }
      />

      <StyledReportWrapper id="ma-content-root">
        {/* TODO: Include this in the Header component above using title and actions */}
        {/* <ReportHeaderComponent
            toggleZendeskDialog={toggleZendeskDialog}
            reportName={headerTitle}
            actionButton={
              showEmailScheduler ? (
                <Button
                  variant="primary"
                  size="default"
                  onClick={() => {
                    handleScheduleNew();
                  }}
                  leading={<span>+</span>}
                >
                  Schedule email
                </Button>
              ) : undefined
            }
          /> */}

        {showDialogFeedback === DialogFeedbackType.SUCCESS && (
          <BannerWrapper>
            <Banner variant="success" onDismiss={handleDismiss} dismissible>
              <Banner.Title>Success</Banner.Title>
              <Banner.Description>
                Your request has been sent. Soon we'll get in touch.
              </Banner.Description>
            </Banner>
          </BannerWrapper>
        )}

        {showDialogFeedback === DialogFeedbackType.FAILED && (
          <BannerWrapper>
            <Banner variant="danger" onDismiss={handleDismiss} dismissible>
              <Banner.Title>Your request was not sent.</Banner.Title>
              <Banner.Description>
                Please contact Loadsmart team to handle the request.
              </Banner.Description>
            </Banner>
          </BannerWrapper>
        )}

        {selectedReport && (
            <ReportComponent
              selectedReport={selectedReport}
              accessToken={pbiToken?.embedToken}
              isReportLoading={isReportsLoading}
              isTokenLoading={isTokenLoading}
              isDefaultReport={selectedReport?.roleName === uniqueRoles[0]}
            />
          )}

        {showRefreshButton && (
          <Banner variant="warning" onDismiss={handleDismiss} dismissible>
            <Banner.Icon>
              <VscGraph color="#005454" size="24px" />
            </Banner.Icon>
            <Banner.Title>This is a subset of data</Banner.Title>
            <Banner.Description>
              The data on this report was previously filtered by FIAI
            </Banner.Description>
            <Banner.Actions>
              <Banner.ActionPrimary onClick={handleRefreshReport}>
                Remove preset filters
              </Banner.ActionPrimary>
            </Banner.Actions>
          </Banner>
        )}

      </StyledReportWrapper>

      <ZendeskDialog
        open={showRequestDialog}
        requestType={requestType}
        disabled={isSendingRequest}
        onCancelRequest={() => setShowRequestDialog(false)}
        onSendRequest={handleOnSendFeedback}
        setRequestType={setRequestType}
      />
    </AppLayout>
  )
}

export default ManagedAnalytics
